import { SetableInspectionFields } from 'store/inspection';
import { InspectionTemplateItemModel } from 'components/dataProviders/withRequiredInspectionTemplate/types';
import {
  COMPLIANCE_CHECK_FORM_COMMENT,
} from 'shared/domain/protocolAnswer/formKeys';
import { AnswerType } from 'shared/domain/checklistAnswer/types/answerType';
import {
  requiredValidation,
  ErrorPresentation,
  notEmptyArrayValidation,
  maxArraySizeValidation,
} from 'helpers/validators';
import { getRichTextLength } from 'shared/utils/richText';
import { FieldSize } from 'shared/types/fieldSize';
import { HashMap } from 'shared/types/commonView';
import { InspectionFlatForm } from './model';
import { ProtocolItemModel, InspectionStatus } from 'shared/domain/inspection/inspectionModel';
import { protocolItemsByChecklistItemId } from 'shared/domain/inspection/mapping/protocolItemsByChecklistItemId';
import {
  buildComplianceAnswerFormKey,
  buildNumericAnswerFormKey,
  buildOptionSelectionAnswerFormKey,
  buildTextAnswerFormKey,
  isProtocolAnswerFilled,
} from 'shared/domain/protocolAnswer';
import { ValidationTranslationKeys } from 'intl/keys';
import {
  InspectionFormValidation,
  InspectionValidation,
  InspectionValidationFunction,
} from './types';

type FinishProtocolValidationTemplate = {
  checklist: Array<{
    _id: string;
    deleted?: boolean;
    answers: Array<{
      _id: string;
      isRequired: boolean;
      type: AnswerType;
    }>;
  }>;
};

type GetProtocolItemsForFinish = () => ProtocolItemModel[] | undefined;

const validation: InspectionValidation = {
  comment: commentValidation,
  sites: sitesValidation,
  levels: levelsValidation,
  contracts: contractsValidation,
  controlledParty: controlledPartyValidation,
  inspectionDate: pass,
  workTypes: workTypesValidation,
};

export function validateInspectionField(
  key: keyof SetableInspectionFields,
  value: SetableInspectionFields[keyof SetableInspectionFields],
  ...args: unknown[]
): ErrorPresentation {
  if (key.startsWith(`${COMPLIANCE_CHECK_FORM_COMMENT}`)) {
    return validation.comment(value);
  }

  return validation[key]?.(value, ...args);
}

export function createInspectionFormValidation(
  validation: InspectionValidationFunction,
  template: InspectionTemplateItemModel | undefined,
  getProtocolItemsForFinish?: GetProtocolItemsForFinish
): InspectionFormValidation {
  return (setErrors, form, options): boolean => {
    const finish = options?.finish;
    const protocolItems =
      finish && getProtocolItemsForFinish
        ? getProtocolItemsForFinish()
        : undefined;
    const errors = validation(finish, template, form, protocolItems);
    setErrors(errors);
    return Boolean(Object.keys(errors).length);
  };
}

export function inspectionValidation(
  finish: undefined | boolean,
  template: InspectionTemplateItemModel | undefined,
  form: InspectionFlatForm,
  protocolItems?: ProtocolItemModel[]
): HashMap<ErrorPresentation> {
  return validate(finish, template, validation, form, protocolItems);
}

function validate(
  finish: boolean | undefined,
  template: InspectionTemplateItemModel | undefined,
  validationObject: HashMap<(value: unknown) => ErrorPresentation>,
  form: InspectionFlatForm,
  protocolItems?: ProtocolItemModel[]
): HashMap<ErrorPresentation> {
  const formErrors = Object.entries(validationObject).reduce<
    HashMap<ErrorPresentation>
  >((errors, [key, findError]) => {
    const error = findError(form[key]);
    if (error) {
      errors[key] = error;
    }
    return errors;
  }, {} as HashMap<ErrorPresentation>);

  const commentErrors = template?.checklist.reduce(
    (protocolErrors, checklistItem) => {
      if (checklistItem.deleted) {
        return protocolErrors;
      }
      const protocolCommentKey = `${COMPLIANCE_CHECK_FORM_COMMENT}-${checklistItem._id}`;
      const protocolItemComment = form[protocolCommentKey];
      const errorComment = commentValidation(protocolItemComment);
      if (errorComment) {
        protocolErrors[protocolCommentKey] = errorComment;
      }

      return protocolErrors;
    },
    {} as HashMap<ErrorPresentation>
  );

  if (!finish) {
    return { ...formErrors, ...commentErrors };
  }

  const protocolErrors = collectFinishProtocolAnswerErrors(
    template,
    form,
    protocolItems
  );

  return {
    ...formErrors,
    ...commentErrors,
    ...protocolErrors,
  };
}

export function hasMissingRequiredFinishProtocolAnswers(
  template: FinishProtocolValidationTemplate | undefined,
  form: InspectionFlatForm,
  protocolItems?: ProtocolItemModel[],
  notApplicableChecklistItemIds?: ReadonlySet<string>
): boolean {
  if (!template) {
    return false;
  }

  return (
    Object.keys(
      collectFinishProtocolAnswerErrors(
        template,
        form,
        protocolItems,
        notApplicableChecklistItemIds
      )
    ).length > 0
  );
}

type SectionParentRef = {
  _id: string;
  parentSectionId: string | null;
};

/** Checklist items that sit in a section marked not applicable, including nested sections. */
export function checklistItemIdsCoveredByNotApplicableSections(
  sections: readonly SectionParentRef[],
  checklistItems: readonly SectionParentRef[],
  notApplicableSectionIds: ReadonlySet<string>
): Set<string> {
  const coveredSectionIds = new Set(notApplicableSectionIds);
  let grew = true;

  while (grew) {
    grew = false;
    sections.forEach((section) => {
      if (
        section.parentSectionId != null &&
        coveredSectionIds.has(section.parentSectionId) &&
        !coveredSectionIds.has(section._id)
      ) {
        coveredSectionIds.add(section._id);
        grew = true;
      }
    });
  }

  const checklistItemIds = new Set<string>();
  checklistItems.forEach((item) => {
    if (
      item.parentSectionId != null &&
      coveredSectionIds.has(item.parentSectionId)
    ) {
      checklistItemIds.add(item._id);
    }
  });

  return checklistItemIds;
}

export function shouldReopenInspectionAfterRequiredAnswersCleared(
  status: InspectionStatus,
  hadMissingRequiredAnswers: boolean,
  hasMissingRequiredAnswersNow: boolean
): boolean {
  return (
    status === InspectionStatus.completed &&
    !hadMissingRequiredAnswers &&
    hasMissingRequiredAnswersNow
  );
}

function collectFinishProtocolAnswerErrors(
  template: FinishProtocolValidationTemplate | undefined,
  form: InspectionFlatForm,
  protocolItems?: ProtocolItemModel[],
  notApplicableChecklistItemIds?: ReadonlySet<string>
): HashMap<ErrorPresentation> {
  if (!template) {
    return {};
  }

  const protocolByChecklistItemId = protocolItemsByChecklistItemId(protocolItems);

  return template.checklist.reduce((protocolErrors, checklistItem) => {
    if (checklistItem.deleted) {
      return protocolErrors;
    }

    if (notApplicableChecklistItemIds?.has(checklistItem._id)) {
      return protocolErrors;
    }

    const protocolItem = protocolByChecklistItemId.get(checklistItem._id);

    checklistItem.answers.forEach((answerConfig) => {
      if (!answerConfig.isRequired) {
        return;
      }

      if (
        isProtocolAnswerFilled(
          form,
          checklistItem._id,
          answerConfig._id,
          answerConfig.type,
          protocolItem
        )
      ) {
        return;
      }

      if (answerConfig.type === AnswerType.Compliance) {
        protocolErrors[
          buildComplianceAnswerFormKey(checklistItem._id, answerConfig._id)
        ] = requiredValidation(undefined);
        return;
      }

      if (answerConfig.type === AnswerType.Numeric) {
        protocolErrors[
          buildNumericAnswerFormKey(checklistItem._id, answerConfig._id)
        ] = requiredValidation(undefined);
        return;
      }

      if (answerConfig.type === AnswerType.Text) {
        protocolErrors[
          buildTextAnswerFormKey(checklistItem._id, answerConfig._id)
        ] = requiredValidation(undefined);
        return;
      }

      if (answerConfig.type === AnswerType.OptionSelection) {
        protocolErrors[
          buildOptionSelectionAnswerFormKey(checklistItem._id, answerConfig._id)
        ] = requiredValidation(undefined);
      }
    });

    return protocolErrors;
  }, {} as HashMap<ErrorPresentation>);
}

function commentValidation(value: unknown): ErrorPresentation {
  if (typeof value !== 'string' || !value) {
    return undefined;
  }

  const plainTextLength = getRichTextLength(value);
  if (plainTextLength > FieldSize.RICH_TEXT) {
    return {
      helperText: { id: ValidationTranslationKeys.stringLengthMaximumWeb },
      values: { maxLength: FieldSize.RICH_TEXT },
    };
  }

  return undefined;
}

function sitesValidation(value: unknown): ErrorPresentation {
  return requiredValidation(value) || notEmptyArrayValidation(value);
}

function levelsValidation(value: unknown): ErrorPresentation {
  return requiredValidation(value) || notEmptyArrayValidation(value);
}

function contractsValidation(value: unknown): ErrorPresentation {
  return value === undefined
    ? undefined
    : maxArraySizeValidation(5, value);
}

function controlledPartyValidation(value: unknown): ErrorPresentation {
  return value === undefined
    ? undefined
    : maxArraySizeValidation(10, value);
}

function workTypesValidation(value: unknown): ErrorPresentation {
  return value === undefined
    ? undefined
    : maxArraySizeValidation(5, value);
}

function pass(..._args: unknown[]): undefined {
  return undefined;
}
